/**
 * Retailer Only
 *
 * Products tagged `retailer-only` stay fully visible across collection, PDP and
 * search, but cannot be bought online. Every add-to-cart affordance renders an
 * inert "Retailer Only" label instead, and the back-in-stock form is suppressed.
 *
 * This takes priority over stock state: a product that is both tagged and sold
 * out shows "Retailer Only", never "Sold Out".
 */

export const RETAILER_ONLY_TAG = 'retailer-only';
export const RETAILER_ONLY_LABEL = 'Retailer Only';

/**
 * Marker class identifying a Button block as the add-to-cart button.
 *
 * The generic Button block renders every CTA on the PDP (Find in Store, Size
 * Guide, …), so the retailer-only treatment must not apply to all of them.
 * The add-to-cart button is the one carrying this class in `button_classes`.
 */
export const ADD_TO_CART_CLASS = 'add-to-cart';

/**
 * True when a Button block is the add-to-cart button.
 *
 * Matches on whole class tokens only, so `add-to-cart` matches but an unrelated
 * class such as `sticky-add-to-cart-button` does not.
 */
export const isAddToCartButton = (buttonClasses?: string | null): boolean =>
  String(buttonClasses || '')
    .split(/\s+/)
    .includes(ADD_TO_CART_CLASS);

/**
 * Tags arrive either as an array (product JSON) or a comma separated string
 * (some Liquid serialisations), so normalise before matching. Matching is
 * case-insensitive and trims whitespace to survive admin entry variations.
 */
export const isRetailerOnly = (product?: { tags?: string[] | string } | null): boolean => {
  const tags = product?.tags;

  if (!tags) return false;

  const list = Array.isArray(tags) ? tags : String(tags).split(',');

  return list.some((tag) => String(tag).trim().toLowerCase() === RETAILER_ONLY_TAG);
};

export default isRetailerOnly;
